// Route guards by AuthStatus (WEB_PLAN.md §3.1, §5.0). Every guard shows the same full-page states while the account
// can't be placed yet: a loading screen while the session is restored or memberships are read, and a Try again
// screen when memberships couldn't be read (the stored session stays; nothing is wiped). A suspended account sees
// "Your access was paused" wherever it lands (via `/`).

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { stashInviteCode } from '@/features/welcome';
import { AccessPausedScreen, FullPageLoading, MembershipRetryScreen } from '@/features/auth/components/GateScreens';
import { useAuth } from './AuthProvider';

/** Loading or "couldn't check" while the status is `loading`; null once the status is settled. */
function pendingScreen(status: string, error: string | null): ReactNode | null {
  if (status !== 'loading') return null;
  return error ? <MembershipRetryScreen /> : <FullPageLoading />;
}

/** Only for signed-out visitors (sign-in pages); signed-in people go to / or /welcome. */
export function RequireSignedOut({ children }: { children: ReactNode }) {
  const { status, error } = useAuth();
  const pending = pendingScreen(status, error);
  if (pending) return pending;
  if (status === 'ready' || status === 'suspended') return <Navigate to="/" replace />;
  if (status === 'noCompany') return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

/** Signed in without a company (/welcome). */
export function RequireNoCompany({ children }: { children: ReactNode }) {
  const { status, error } = useAuth();
  const { hash } = useLocation();
  const pending = pendingScreen(status, error);
  if (pending) return pending;
  if (status === 'signedOut') {
    // An invite link (/welcome#code=…) opened while signed out: the fragment is lost on the way to /sign-in, so keep
    // the code for /welcome to read back after sign-in.
    stashInviteCode(hash);
    return <Navigate to="/sign-in" replace />;
  }
  if (status === 'ready' || status === 'suspended') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Signed in with an active membership (the console). */
export function RequireCompany({ children }: { children: ReactNode }) {
  const { status, error } = useAuth();
  const pending = pendingScreen(status, error);
  if (pending) return pending;
  if (status === 'signedOut') return <Navigate to="/sign-in" replace />;
  if (status === 'noCompany') return <Navigate to="/welcome" replace />;
  if (status === 'suspended') return <AccessPausedScreen />;
  return <>{children}</>;
}
